import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import { useTransactionInitiateMutation } from "Api/Mutations/Transactions/useTransactionInitiateMutation";
import { useTransactionPay } from "Api/Mutations/Transactions/useTransactionPay";
import { useAmount } from "Hooks/useAmount";
import type { BankEnvironment } from "Infrastructure/Api/Api";
import {
  type ErrorType,
  getTransactionErrorMessage,
  handleTransactionPayError,
} from "Utils/TransactionErrorsUtils";
import { useResource } from "Translations/Resources";
import { useState } from "react";
import { toast } from "sonner";

type TransferParams = {
  amount: number;
  senderBankAccountID: number;
  senderEnvironment: BankEnvironment;
  receiverBankAccountID: number;
  note?: string | null;
};

export const useTransferBetweenAccounts = () => {
  const { t } = useResource();
  // Stays true once a redirect is committed — mutation isPending flips false before the navigation
  // completes, which would re-enable the button and make the tap look like a no-op (same as usePaymentSubmit).
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { mutate: payTransaction, isPending: isPayPending } =
    useTransactionPay();

  const { mutate: initiateTransaction, isPending: isInitiatePending } =
    useTransactionInitiateMutation();

  // ZS-1662: an invalid/expired PISP token (RB) fails /pay with `pispAuthentication` — same as the
  // classic payment, send the user to the bank to re-authorize; the callback lands back on the
  // transaction detail where the transfer is paid.
  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate({
    onError: () => setIsRedirecting(false),
  });

  const toAmountWithCurrency = useAmount();

  const transfer = ({
    senderBankAccountID,
    senderEnvironment,
    receiverBankAccountID,
    amount,
    note,
  }: TransferParams) => {
    initiateTransaction(
      {
        amount: toAmountWithCurrency(amount),
        receiverBankAccountID,
        noteForCreditor: note ?? null,
      },
      {
        onError: (error) => {
          const message = getTransactionErrorMessage(
            error as unknown as ErrorType,
            t,
          );
          toast.error(message);
        },
        onSuccess: ({ transaction }) => {
          payTransaction(
            { publicID: transaction.publicID, senderBankAccountID },
            {
              onSuccess: ({ signingUrl, isServerAuthorized }) => {
                // useTransactionPay.onSuccess redirects (window.location.href); keep the button loading through it.
                if (signingUrl || isServerAuthorized) {
                  setIsRedirecting(true);
                }
              },
              onError: (error) => {
                const e = error as unknown as ErrorType;
                if (e.data?.errors?.pispAuthentication) {
                  setIsRedirecting(true);
                  fetchAndRedirectToPISPURL({
                    environment: senderEnvironment,
                    transactionPublicID: transaction.publicID,
                  });
                  return;
                }
                handleTransactionPayError(e, t);
              },
            },
          );
        },
      },
    );
  };

  return {
    transfer,
    isPending: isInitiatePending || isPayPending || isRedirecting,
  };
};
